import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import {
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import SaveIcon from '@material-ui/icons/Save';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { db, storage } from 'common/config/Firebase';
import './Settings.css';
import EditableField from '../services/editableFields';
import { ICON_MAP, ICON_OPTIONS } from 'common/config/infoIconsConfig';
import { TEST_ICON_MAP, TEST_ICON_OPTIONS, DEFAULT_TEST_ITEMS } from 'common/config/testIconsConfig';
import UsersManagementSection from '../components/UsersManagementSection';


export default function SettingsContainer({ roleUser }) {
    const canEdit = roleUser === 'super';

    const [settings, setSettings] = useState({
        companyName: '',
        taxId: '',
        address: '',
        postalCode: '',
        city: '',
        province: '',
        email: '',
        phone: '',
        website: '',
        bank: '',
        accountNumber: '',
        invoiceFooter: '',
        ticketFooter: '',
        reportFooter: '',
        logoUrl: '',
        whatsappReviewMsg: '',
        whatsappRepairedMsg: '',
        // --- NUEVOS CAMPOS REGISTRO MERCANTIL ---
        mercantileBook: '',
        mercantilePage: '',
        mercantileSheet: '',
        // --- NUEVO CAMPO INFORME ---
        serviceQualityText: '',
        // --- ICONOS INFO ---
        infoIcons: [],
        // --- TESTS REPARACIÓN ---
        testItems: DEFAULT_TEST_ITEMS,
    });

    const [lastSaved, setLastSaved] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);

    // estado para saber si el usuario ha pulsado botón editar
    const [isEditing, setIsEditing] = useState(false);

    // callback que te pasan los campos cuando el usuario pulsa editar
    const notifyBeginEdit = () => {
        setIsEditing(true);
    };
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: '', error: false });
    const [tab, setTab] = useState('general');

    // Lectura en tiempo real
    useEffect(() => {
        const unsub = db
            .collection('meta')
            .doc('settings')
            .onSnapshot(
                (snap) => {
                    const data = snap.exists ? snap.data() : {};
                    setSettings((prev) => ({ ...prev, ...data }));
                    setLastSaved({ ...data });
                    setLoading(false);
                },
                (err) => {
                    console.error(err);
                    setSnack({ open: true, msg: 'Error cargando configuración', error: true });
                    setLoading(false);
                }
            );
        return () => unsub();
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setSettings((s) => ({ ...s, [name]: value }));
    };

    const handleInfoIconChange = (index, field, value) => {
        const newIcons = [...(settings.infoIcons || [])];
        newIcons[index] = { ...newIcons[index], [field]: value };
        setSettings(s => ({ ...s, infoIcons: newIcons }));
        setIsEditing(true);
    };

    const handleAddInfoIcon = () => {
        const newIcons = [...(settings.infoIcons || []), { icon: '', title: '', imageUrl: '' }];
        setSettings(s => ({ ...s, infoIcons: newIcons }));
        setIsEditing(true);
    };

    const handleRemoveInfoIcon = (index) => {
        const newIcons = (settings.infoIcons || []).filter((_, i) => i !== index);
        setSettings(s => ({ ...s, infoIcons: newIcons }));
        setIsEditing(true);
    };

    const handleTestItemChange = (index, field, value) => {
        const newItems = [...(settings.testItems || [])];
        newItems[index] = { ...newItems[index], [field]: value };
        setSettings(s => ({ ...s, testItems: newItems }));
        setIsEditing(true);
    };

    const handleAddTestItem = () => {
        setSettings(s => ({ ...s, testItems: [...(s.testItems || []), { emoji: '', name: '' }] }));
        setIsEditing(true);
    };

    const handleRemoveTestItem = (index) => {
        setSettings(s => ({ ...s, testItems: (s.testItems || []).filter((_, i) => i !== index) }));
        setIsEditing(true);
    };

    const handleIconImageUpload = async (index, e) => {
        if (!canEdit) return;
        const inputEl = e.target;
        const file = inputEl?.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const storageRef = storage.ref();
            const fileRef = storageRef.child(`info-icons/${index}_${file.name}`);
            await fileRef.put(file);
            const url = await fileRef.getDownloadURL();
            handleInfoIconChange(index, 'imageUrl', url);
            setSnack({ open: true, msg: 'Imagen subida correctamente', error: false });
        } catch (err) {
            console.error(err);
            setSnack({ open: true, msg: 'Error al subir la imagen', error: true });
        } finally {
            setUploading(false);
            if (inputEl) inputEl.value = '';
        }
    };

    const validate = () => {
        if (!settings.companyName?.trim()) return 'El nombre fiscal es obligatorio';
        if (!settings.taxId?.trim()) return 'El CIF/NIF es obligatorio';
        return null;
    };

    const onSave = async () => {
        if (!canEdit) return;
        const err = validate();
        if (err) return setSnack({ open: true, msg: err, error: true });

        setSaving(true);
        try {
            await db.collection('meta').doc('settings').set(
                { ...settings, updatedAt: new Date() },
                { merge: true }
            );
            setSnack({ open: true, msg: 'Configuración guardada', error: false });
            setResetKey(k => k + 1);
            setLastSaved({ ...settings });
            setIsEditing(false);
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: 'Error al guardar', error: true });
        } finally {
            setSaving(false);
        }
    };

    // Cambiar de pestaña con confirmación si hay ediciones o cambios sin guardar
    const handleTabChange = (next) => {
        if (isEditing) {
            setPendingTab(next);
            setConfirmOpen(true);
            return;
        }
        setTab(next);
    };


    const handleLogoUpload = async (e) => {
        if (!canEdit) return;

        // captura el input ANTES de cualquier await (evita el pooling de React)
        const inputEl = e.target;
        const file = inputEl?.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const storageRef = storage.ref();
            const fileRef = storageRef.child(`logos/${file.name}`);
            await fileRef.put(file);
            const url = await fileRef.getDownloadURL();

            // Guarda en Firestore y deja que onSnapshot actualice el estado
            await db.collection('meta').doc('settings').set(
                { logoUrl: url, updatedAt: new Date() },
                { merge: true }
            );

            setSnack({ open: true, msg: 'Logo actualizado correctamente', error: false });
        } catch (err) {
            console.error(err);
            setSnack({ open: true, msg: 'Error al subir el logo', error: true });
        } finally {
            setUploading(false);
            // usa la referencia capturada, no "e.target"
            if (inputEl) inputEl.value = '';
        }
    };

    if (loading) {
        return (
            <div style={{ padding: 16 }}>
                <CircularProgress />
            </div>
        );
    }

    // Secciones
    const renderSection = () => {
        switch (tab) {
            case 'general':
                return (
                    <div className="section">
                        {/* GENERAL */}
                        <h3>General</h3>

                        {/* Datos fiscales */}
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Nombre fiscal"
                                    name="companyName"
                                    value={settings.companyName}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field">
                                <EditableField
                                    label="CIF/NIF"
                                    name="taxId"
                                    value={settings.taxId}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>

                        {/* Registro mercantil */}
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Tomo registro mercantil"
                                    name="mercantileBook"
                                    value={settings.mercantileBook}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field">
                                <EditableField
                                    label="Folio registro mercantil"
                                    name="mercantilePage"
                                    value={settings.mercantilePage}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field">
                                <EditableField
                                    label="Hoja registro mercantil"
                                    name="mercantileSheet"
                                    value={settings.mercantileSheet}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>

                        {/* Contacto */}
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Correo electrónico"
                                    name="email"
                                    value={settings.email}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    type="email"
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field">
                                <EditableField
                                    label="Teléfono"
                                    name="phone"
                                    value={settings.phone}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    type="tel"
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field">
                                <EditableField
                                    label="Página web"
                                    name="website"
                                    value={settings.website}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    type="url"
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>

                        {/* Calle / Número / Piso(Bajo, 1, 2...) */}
                        <div className="field-group">
                            <div className="field" style={{ flex: 1 }}>
                                <EditableField
                                    label="Dirección"
                                    name="address"
                                    value={settings.address}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>

                        {/* CP / Localidad / Provincia */}
                        <div className="field-group">
                            <div className="field" style={{ flex: 0.4 }}>
                                <EditableField
                                    label="Código Postal"
                                    name="postalCode"
                                    value={settings.postalCode}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <EditableField
                                    label="Localidad"
                                    name="city"
                                    value={settings.city}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <EditableField
                                    label="Provincia"
                                    name="province"
                                    value={settings.province}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>

                        {/* Datos bancarios */}
                        <div className="field-group">
                            <div className="field" style={{ flex: 0.6 }}>
                                <EditableField
                                    label="Banco"
                                    name="bank"
                                    value={settings.bank}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <EditableField
                                    label="Número de cuenta"
                                    name="accountNumber"
                                    value={settings.accountNumber}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'logo':
                return (
                    <div className="section">
                        {/* LOGO */}
                        <h3>Logo</h3>
                        <div className="logo-section">
                            {settings.logoUrl ? (
                                <img
                                    src={settings.logoUrl}
                                    alt="Logo actual"
                                    className="logo-preview"
                                />
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No hay logo cargado
                                </Typography>
                            )}
                            {canEdit && (
                                <div style={{ marginTop: '1rem' }}>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="logo-upload"
                                        type="file"
                                        onChange={handleLogoUpload}
                                    />
                                    <label htmlFor="logo-upload">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            component="span"
                                            startIcon={<CloudUploadIcon />}
                                            disabled={uploading}
                                        >
                                            {uploading ? 'Subiendo…' : 'Subir logo'}
                                        </Button>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'tickets':
                return (
                    <div className="section">
                        {/* TICKETS */}
                        <h3>Tickets</h3>
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Pie de ticket"
                                    name="ticketFooter"
                                    value={settings.ticketFooter}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    multiline
                                    rows={5}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'facturas':
                return (
                    <div className="section">
                        {/* FACTURAS */}
                        <h3>Facturas</h3>
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Pie de factura"
                                    name="invoiceFooter"
                                    value={settings.invoiceFooter}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    multiline
                                    rows={5}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'informes':
                return (
                    <div className="section">
                        {/* INFORMES */}
                        <h3>Informes</h3>
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Texto calidad del servicio"
                                    name="serviceQualityText"
                                    value={settings.serviceQualityText}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    multiline
                                    rows={5}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Pie de informe"
                                    name="reportFooter"
                                    value={settings.reportFooter}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    multiline
                                    rows={5}
                                    onBeginEdit={notifyBeginEdit}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'whatsapp':
                return (
                    <div className="section">
                        {/* MENSAJES WHATSAPP */}
                        <h3>WhatsApp</h3>
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Mensaje reseña    "
                                    name="whatsappReviewMsg"
                                    value={settings.whatsappReviewMsg}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                    multiline
                                    rows={5}
                                />
                            </div>
                        </div>
                        <div className="field-group">
                            <div className="field">
                                <EditableField
                                    label="Mensaje reparado"
                                    name="whatsappRepairedMsg"
                                    value={settings.whatsappRepairedMsg}
                                    onChange={onChange}
                                    canEdit={canEdit}
                                    resetKey={resetKey}
                                    onBeginEdit={notifyBeginEdit}
                                    multiline
                                    rows={5}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'iconos': {
                const icons = settings.infoIcons || [];
                return (
                    <div className="section">
                        <h3>Iconos Info</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                            Configura hasta 4 iconos que aparecerán en la parte superior de reparaciones. Cada icono abre una imagen al hacer clic.
                        </p>

                        {icons.map((item, index) => {
                            const PreviewIcon = item.icon ? ICON_MAP[item.icon] : null;
                            return (
                                <div key={index} style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    marginBottom: '1rem',
                                    padding: '1rem',
                                    border: '1px solid #c5cae9',
                                    borderRadius: '4px',
                                    backgroundColor: '#fff',
                                }}>
                                    {/* Selector de icono */}
                                    <FormControl variant="outlined" size="small" style={{ minWidth: 180 }} disabled={!canEdit}>
                                        <InputLabel>Icono</InputLabel>
                                        <Select
                                            value={item.icon || ''}
                                            onChange={e => handleInfoIconChange(index, 'icon', e.target.value)}
                                            label="Icono"
                                        >
                                            {ICON_OPTIONS.map(opt => {
                                                const Ic = ICON_MAP[opt.name];
                                                return (
                                                    <MenuItem key={opt.name} value={opt.name}>
                                                        <Ic style={{ marginRight: 8, verticalAlign: 'middle', fontSize: 18 }} />
                                                        {opt.label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>

                                    {/* Título / tooltip */}
                                    <TextField
                                        label="Título (tooltip)"
                                        value={item.title || ''}
                                        onChange={e => handleInfoIconChange(index, 'title', e.target.value)}
                                        disabled={!canEdit}
                                        variant="outlined"
                                        size="small"
                                        style={{ flex: 1, minWidth: 180 }}
                                    />

                                    {/* Imagen */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {item.imageUrl ? (
                                            <img
                                                src={item.imageUrl}
                                                alt="preview"
                                                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #c5cae9' }}
                                            />
                                        ) : (
                                            PreviewIcon && <PreviewIcon style={{ fontSize: 48, color: '#c5cae9' }} />
                                        )}
                                        {canEdit && (
                                            <>
                                                <input
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    id={`icon-img-${index}`}
                                                    type="file"
                                                    onChange={e => handleIconImageUpload(index, e)}
                                                />
                                                <label htmlFor={`icon-img-${index}`}>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        component="span"
                                                        startIcon={<CloudUploadIcon />}
                                                        disabled={uploading}
                                                        size="small"
                                                    >
                                                        {item.imageUrl ? 'Cambiar' : 'Subir imagen'}
                                                    </Button>
                                                </label>
                                            </>
                                        )}
                                    </div>

                                    {/* Eliminar */}
                                    {canEdit && (
                                        <IconButton
                                            onClick={() => handleRemoveInfoIcon(index)}
                                            size="small"
                                            title="Eliminar icono"
                                            style={{ color: '#e53935' }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </div>
                            );
                        })}

                        {canEdit && icons.length < 4 && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddCircleIcon />}
                                onClick={handleAddInfoIcon}
                                style={{ marginTop: '0.5rem' }}
                            >
                                Añadir icono
                            </Button>
                        )}
                    </div>
                );
            }

            case 'reparaciones': {
                const items = settings.testItems || [];
                return (
                    <div className="section">
                        <h3>Test de reparación</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                            Configura la lista de pruebas que aparecerá al revisar una reparación. Cada ítem tiene un emoji y un nombre.
                        </p>
                        {items.map((item, index) => {
                            const SelectedIcon = item.icon ? TEST_ICON_MAP[item.icon] : null
                            return (
                                <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <FormControl variant="outlined" size="small" style={{ minWidth: 210 }} disabled={!canEdit}>
                                        <InputLabel>Icono</InputLabel>
                                        <Select
                                            value={item.icon || ''}
                                            onChange={e => handleTestItemChange(index, 'icon', e.target.value)}
                                            label="Icono"
                                            renderValue={(val) => {
                                                const Ic = TEST_ICON_MAP[val]
                                                return (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                        {Ic && <Ic style={{ fontSize: 20 }} />}
                                                    </span>
                                                )
                                            }}
                                        >
                                            {TEST_ICON_OPTIONS.map(opt => {
                                                const Ic = TEST_ICON_MAP[opt.name]
                                                return (
                                                    <MenuItem key={opt.name} value={opt.name}>
                                                        <Ic style={{ marginRight: 8, verticalAlign: 'middle', fontSize: 18 }} />
                                                        {opt.label}
                                                    </MenuItem>
                                                )
                                            })}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label="Nombre"
                                        value={item.name || ''}
                                        onChange={e => handleTestItemChange(index, 'name', e.target.value)}
                                        disabled={!canEdit}
                                        variant="outlined"
                                        size="small"
                                        style={{ flex: 1 }}
                                    />
                                    {canEdit && (
                                        <IconButton
                                            onClick={() => handleRemoveTestItem(index)}
                                            size="small"
                                            title="Eliminar"
                                            style={{ color: '#e53935' }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </div>
                            )
                        })}
                        {canEdit && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddCircleIcon />}
                                onClick={handleAddTestItem}
                                style={{ marginTop: '0.5rem' }}
                            >
                                Añadir prueba
                            </Button>
                        )}
                    </div>
                );
            }

            case 'usuarios':
                return <UsersManagementSection />;

            default:
                return null;
        }
    };

    return (
        <div className="settings-container">
            {!canEdit && (
                <div className="readonly-banner">
                    <LockIcon style={{ marginRight: 8 }} />
                    Estás en modo lectura. Necesitas un rol superior para editar estos campos.
                </div>
            )}

            {/* Selector de pestañas */}
            <div className="tab-selector">
                {['general', 'logo', 'tickets', 'facturas', 'informes', 'whatsapp', 'iconos', 'reparaciones', ...(roleUser === 'super' ? ['usuarios'] : [])].map((key) => (
                    <button
                        key={key}
                        className={`tab-button ${tab === key ? 'active' : ''}`}
                        onClick={() => handleTabChange(key)}
                    >
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                    </button>
                ))}
            </div>

            {renderSection()}

            {canEdit && tab !== 'logo' && tab !== 'usuarios' && (
                <div className="actions">
                    <Button
                        title="Save"
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={onSave}
                        disabled={saving}
                    >
                        {saving ? 'Guardando…' : 'Guardar cambios'}
                    </Button>
                </div>
            )}

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                PaperProps={{ className: 'confirm-dialog-paper' }}
            >
                <DialogTitle className="confirm-dialog-title">
                    ¿Guardar o descartar cambios?
                </DialogTitle>
                <DialogContent className="confirm-dialog-content">
                    Tienes cambios sin guardar o campos en edición.
                </DialogContent>
                <DialogActions className="confirm-dialog-actions">
                    <Button
                        onClick={() => {
                            // Descartar: vuelve a lo último guardado y cierra edición
                            if (lastSaved) setSettings({ ...lastSaved });
                            setResetKey(k => k + 1);
                            setIsEditing(false);
                            setConfirmOpen(false);
                            if (pendingTab) setTab(pendingTab);
                            setPendingTab(null);
                        }}
                    >
                        Descartar
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={async () => {
                            await onSave();
                            setIsEditing(false);
                            setConfirmOpen(false);
                            if (pendingTab) setTab(pendingTab);
                            setPendingTab(null);
                        }}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
                message={snack.msg}
                ContentProps={{ style: { backgroundColor: snack.error ? '#c62828' : '#2e7d32' } }}
            />
        </div>
    );
}